import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Shell from "@/components/app/Shell";
import api, { API } from "@/lib/api";
import { TRIBES } from "@/lib/tribes";
import { Camera, Heart, ArrowLeft } from "lucide-react";

export default function PhotoGallery() {
  const { childId } = useParams();
  const [child, setChild] = useState(null);
  const [certs, setCerts] = useState([]);
  const token = localStorage.getItem("btt_token");

  useEffect(() => {
    Promise.all([api.get(`/children/${childId}`), api.get(`/certificates/${childId}`)]).then(([c, cf]) => {
      setChild(c.data);
      setCerts(cf.data.filter((x) => x.photo_path).sort((a, b) => a.session_number - b.session_number));
    });
  }, [childId]);

  if (!child) return <Shell><div className="text-ink-mid">Loading…</div></Shell>;
  const tribe = TRIBES[child.tribe] || TRIBES.neutral;

  return (
    <Shell>
      <Link to={`/child/${childId}`} className="inline-flex items-center gap-2 text-sm text-ink-mid mb-6"><ArrowLeft size={14} /> {child.name}'s journey</Link>
      <div className="mb-8">
        <div className="text-xs uppercase tracking-widest font-semibold flex items-center gap-2" style={{ color: tribe.subtle }}><Camera size={12} /> Photo Gallery</div>
        <h1 className="font-display text-3xl font-bold text-navy mt-1">Your family's timeline</h1>
        <p className="text-ink-mid mt-2">Every proof photo you've uploaded with a certificate, in order. A quiet timeline of {child.name}'s twelve sessions.</p>
      </div>

      {certs.length === 0 ? (
        <div className="max-w-xl bg-warm-white border border-dashed border-edge rounded-3xl p-8 text-center" data-testid="gallery-empty">
          <Heart className="mx-auto" size={28} style={{ color: tribe.accent }} />
          <div className="mt-4 font-display font-bold text-navy text-lg">No photos yet</div>
          <p className="text-ink-mid text-sm mt-2">Upload a proof photo on any earned certificate to start building your timeline.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-6">
          {certs.map((c) => (
            <Link key={c.id} to={`/child/${childId}/certificate/${c.session_number}`} data-testid={`gallery-cert-${c.session_number}`} className="group block bg-warm-white border rounded-3xl overflow-hidden hover:-translate-y-1 hover:shadow-[0_20px_40px_rgba(44,45,91,0.08)] transition-all" style={{ borderTop: `3px solid ${tribe.accent}`, borderColor: `${tribe.accent}20` }}>
              <div className="aspect-square bg-cream-dark overflow-hidden">
                <img src={`${API}/files/${c.photo_path}?auth=${token}`} alt={`Session ${c.session_number}`} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              </div>
              <div className="p-4">
                <div className="text-xs uppercase tracking-widest text-ink-soft font-semibold">Session {c.session_number}</div>
                <div className="font-display font-bold text-navy mt-1">{new Date(c.date).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}</div>
                {c.donation_credited && <div className="mt-2 inline-flex items-center gap-1 text-xs font-semibold" style={{ color: tribe.accent }}><Heart size={11} /> $1 donated</div>}
              </div>
            </Link>
          ))}
        </div>
      )}
    </Shell>
  );
}
